import { type FC, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import type { DocPage } from '../../docs/types';
import { getCategoryConfig, getDoc } from '../../docs';
import DOC_DIAGRAMS from './DocDiagrams';

interface PublicDocsContentProps {
  page: DocPage;
}

const CodeBlock: FC<{ language: string; children: string }> = ({ language, children }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-4">
      <button
        onClick={handleCopy}
        className="absolute right-2 top-2 p-1.5 rounded bg-gray-700 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-600 hover:text-white"
        title="Copy code"
      >
        <FontAwesomeIcon
          icon={copied ? faCheck : faCopy}
          className={`h-3 w-3 ${copied ? 'text-green-400' : ''}`}
        />
      </button>
      <SyntaxHighlighter
        style={oneDark}
        language={language}
        PreTag="div"
        customStyle={{
          margin: 0,
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
        }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
};

/** Static markdown component overrides (no dependency on page data) */
const staticComponents = {
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-base font-semibold text-gray-900 mt-6 mb-2">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="text-gray-600 leading-relaxed mb-4">{children}</p>
  ),
  a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    if (href?.startsWith('/docs/')) {
      return (
        <Link to={href} className="text-[#9d4edd] hover:underline font-medium">
          {children}
        </Link>
      );
    }
    return (
      <a href={href} className="text-[#9d4edd] hover:underline" target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  code({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) {
    const match = /language-(\w+)/.exec(className || '');
    const content = String(children).replace(/\n$/, '');
    const isBlock = match || content.includes('\n');

    if (!isBlock) {
      return (
        <code className="text-[#9d4edd] bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
          {children}
        </code>
      );
    }

    if (match) {
      return <CodeBlock language={match[1]}>{content}</CodeBlock>;
    }

    return (
      <div className="my-4 rounded-lg bg-gray-900 p-4 overflow-x-auto">
        <pre className="text-sm text-gray-300 font-mono leading-relaxed whitespace-pre">{content}</pre>
      </div>
    );
  },
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1.5">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal list-inside text-gray-600 mb-4 space-y-1.5">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li className="text-gray-600">{children}</li>,
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="text-left text-gray-700 font-medium border border-gray-200 py-2 px-3">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="text-gray-600 border border-gray-200 py-2 px-3">{children}</td>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-4 border-[#9d4edd]/40 pl-4 italic text-gray-500 my-4">{children}</blockquote>
  ),
  hr: () => <hr className="my-8 border-gray-200" />,
};

const PublicDocsContent: FC<PublicDocsContentProps> = ({ page }) => {
  const categoryConfig = getCategoryConfig(page.category);

  const components = useMemo(() => ({
    ...staticComponents,
    h1: ({ children }: { children?: React.ReactNode }) => {
      const Diagram = DOC_DIAGRAMS[page.id];
      return (
        <>
          <h1 className="text-2xl font-bold text-gray-950 mb-4 pb-3 border-b border-gray-200">
            {children}
          </h1>
          {Diagram && <Diagram />}
        </>
      );
    },
  }), [page.id]);

  return (
    <article className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
        <Link to="/docs" className="hover:text-gray-600 transition-colors">
          Docs
        </Link>
        <FontAwesomeIcon icon={faArrowRight} className="h-2 w-2" />
        <span>{categoryConfig?.label || page.category}</span>
        {page.subcategory && (
          <>
            <FontAwesomeIcon icon={faArrowRight} className="h-2 w-2" />
            <span className="capitalize">{page.subcategory}</span>
          </>
        )}
      </div>

      {/* Markdown content */}
      <div className="docs-markdown prose prose-gray max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={components}
        >
          {page.content}
        </ReactMarkdown>
      </div>

      {/* Related docs */}
      {page.related.length > 0 && (() => {
        const validRelated = page.related.filter((id) => getDoc(id));
        return validRelated.length > 0 ? (
          <div className="mt-10 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-500 mb-3">Related</h4>
            <div className="flex flex-wrap gap-2">
              {validRelated.map((relatedId) => {
                const relatedDoc = getDoc(relatedId)!;
                return (
                  <Link
                    key={relatedId}
                    to={`/docs/${relatedId}`}
                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 hover:text-gray-900 transition-colors font-medium"
                  >
                    {relatedDoc.title}
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null;
      })()}

      {/* Tags */}
      {page.tags.length > 0 && (
        <div className="mt-6">
          <div className="flex flex-wrap gap-1.5">
            {page.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs bg-gray-50 text-gray-400 rounded border border-gray-100"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

export default PublicDocsContent;
